import { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CircleNotch, UploadSimple, Sparkle } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

export function CoachStatementUploadDialog({ open, onOpenChange, businessId, account, onUploaded }) {
  const [form, setForm] = useState({ statement_end_balance: "", statement_start_balance: "", statement_end_date: today(), statement_start_date: "", file: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ statement_end_balance: "", statement_start_balance: "", statement_end_date: today(), statement_start_date: "", file: null });
  }, [open, account?.account_id]);

  const acceptedText = useMemo(() => {
    if (!form.file) return "Upload a PDF, CSV, JPG, PNG, WEBP, or HEIC statement.";
    return `${form.file.name} ready to upload.`;
  }, [form.file]);

  const submit = async () => {
    if (!account?.account_id) return toast.error("Pick an account first.");
    if (!form.file) return toast.error("Add the statement file first.");
    if (!form.statement_end_balance) return toast.error("Enter the statement ending balance first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("account_id", account.account_id);
      fd.append("statement_start_balance", form.statement_start_balance || "0");
      fd.append("statement_end_balance", form.statement_end_balance);
      fd.append("statement_start_date", form.statement_start_date || "");
      fd.append("statement_end_date", form.statement_end_date || "");
      fd.append("file", form.file);
      const { data } = await api.post(`/businesses/${businessId}/reconciliation/import-statement`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.rowan_message || "Statement uploaded.");
      onUploaded?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't upload that statement.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-[28px] bg-white border-[1px]" aria-describedby="coach-statement-upload-description" data-testid="coach-statement-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" style={{ color: "var(--bk-text)" }}>Upload a statement without leaving the coach</DialogTitle>
          <DialogDescription id="coach-statement-upload-description" className="text-sm" style={{ color: "var(--bk-text-2)" }}>
            Rowan will use this statement to stop nudging and compare the numbers for <strong style={{ color: "var(--bk-text)" }}>{account?.name || "this account"}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-[24px] border p-4" style={{ borderColor: "rgba(67,56,202,0.14)", background: "linear-gradient(135deg, rgba(238,242,255,0.78), rgba(255,255,255,0.98))" }} data-testid="coach-statement-upload-rowan-card">
          <div className="flex items-start gap-3">
            <Sparkle size={18} weight="fill" color="var(--bk-gold)" className="mt-0.5 shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Rowan nudge</p>
              <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>PDFs and images attach the statement support. CSV files go one step further and preload the reconciliation lines for approval.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Ending balance *
            <input type="number" value={form.statement_end_balance} onChange={(e) => setForm((s) => ({ ...s, statement_end_balance: e.target.value }))} data-testid="coach-statement-ending-balance" className="mt-1.5 w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} placeholder="0.00" />
          </label>
          <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Starting balance
            <input type="number" value={form.statement_start_balance} onChange={(e) => setForm((s) => ({ ...s, statement_start_balance: e.target.value }))} data-testid="coach-statement-starting-balance" className="mt-1.5 w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} placeholder="0.00" />
          </label>
          <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Ending date
            <DateInput value={form.statement_end_date} onChange={(e) => setForm((s) => ({ ...s, statement_end_date: e.target.value }))} data-testid="coach-statement-ending-date" className="mt-1.5 w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
          <label className="text-xs" style={{ color: "var(--bk-text-2)" }}>Starting date
            <DateInput value={form.statement_start_date} onChange={(e) => setForm((s) => ({ ...s, statement_start_date: e.target.value }))} data-testid="coach-statement-starting-date" className="mt-1.5 w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
        </div>

        <label className="block rounded-[24px] border border-dashed p-5 text-center cursor-pointer transition-colors hover:bg-[rgba(246,243,237,0.5)]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} data-testid="coach-statement-file-zone">
          <input type="file" accept=".csv,.pdf,image/*,.heic,.heif" onChange={(e) => setForm((s) => ({ ...s, file: e.target.files?.[0] || null }))} data-testid="coach-statement-file-input" className="hidden" />
          <UploadSimple size={18} weight="bold" className="mx-auto mb-2" />
          <p className="text-sm font-semibold">Drop in the statement file</p>
          <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{acceptedText}</p>
        </label>

        <DialogFooter className="gap-2">
          <button onClick={() => onOpenChange(false)} data-testid="coach-statement-cancel" className="px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Cancel</button>
          <button onClick={submit} disabled={uploading} data-testid="coach-statement-submit" className="bk-btn-primary px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {uploading ? <><CircleNotch size={14} className="animate-spin" /> Uploading…</> : <><UploadSimple size={14} weight="bold" /> Upload statement</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}